import React from 'react';
import { Link } from 'react-router-dom';
import { Table, Button, Modal, Form, Select, notification, Typography, Space, Tag } from 'antd';
import { FileAddOutlined } from '@ant-design/icons';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import { useCompanyId } from '../../lib/books';

import { date as fmtDate, label } from '../../lib/format';
export const LETTER_STATUS_COLORS = {
  DRAFT: 'default',
  SENT: 'blue',
  SIGNED: 'green',
  DECLINED: 'red',
  CANCELLED: 'red',
  SUPERSEDED: 'orange',
};

/** Engagement Letter register (spec §9): generated from accepted proposals, sent, signed. */
export default function EngagementLettersPage() {
  const auth = useAuth();
  const isSystemAdmin = auth.user.role === 'SYSTEM_ADMIN';
  const isStaff = auth.user.role !== 'CLIENT_ADMIN';
  const { companyId, setCompanyId } = useCompanyId();
  const [companies, setCompanies] = React.useState([]);
  const [rows, setRows] = React.useState([]);
  const [clients, setClients] = React.useState([]);
  const [entities, setEntities] = React.useState([]);
  const [statusFilter, setStatusFilter] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [generateOpen, setGenerateOpen] = React.useState(false);
  const [generating, setGenerating] = React.useState(false);
  const [form] = Form.useForm();

  React.useEffect(() => {
    if (isSystemAdmin) {
      request
        .get('/companies')
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            setCompanies(r.data);
            if (!companyId && r.data.length) setCompanyId(r.data[0].id);
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = () => {
    if (!companyId && isStaff) return;
    setLoading(true);
    const q = companyId ? { company_id: companyId } : {};
    Promise.all([
      request.get('/engagement-letters', statusFilter ? { ...q, status: statusFilter } : q).then((r) => r.json()),
      isStaff ? request.get('/clients', q).then((r) => r.json()) : Promise.resolve({ status: false }),
    ])
      .then(([letters, cli]) => {
        if (letters.status) setRows(letters.data);
        if (cli.status) setClients(cli.data);
      })
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(fetchAll, [companyId, statusFilter]);

  const handleClientChange = (clientId) => {
    form.setFieldsValue({ entity_id: null });
    request
      .get(`/clients/${clientId}/entities`)
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          setEntities(r.data);
          const primary = r.data.find((e) => e.is_primary);
          if (primary) form.setFieldsValue({ entity_id: primary.id });
        }
      });
  };

  const handleGenerate = () => {
    form.validateFields().then((values) => {
      setGenerating(true);
      request
        .post('/engagement-letters/generate', values)
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: `Draft ${r.data.letter_number} generated` });
            setGenerateOpen(false);
            fetchAll();
          } else {
            notification.error({ message: r.message });
          }
        })
        .finally(() => setGenerating(false));
    });
  };

  const columns = [
    { title: 'Number', dataIndex: 'letter_number', render: (v, l) => <Link to={`/engagement-letters/${l.id}`}>{v}</Link> },
    { title: 'Issued', dataIndex: 'issue_date', sorter: (a, b) => (a.issue_date < b.issue_date ? -1 : 1), render: (v) => fmtDate(v) },
    { title: 'Client', key: 'client', render: (_, l) => (l.Client ? <Link to={`/clients/${l.Client.id}`}>{l.Client.client_name}</Link> : '-') },
    { title: 'Entity', key: 'entity', render: (_, l) => l.Entity?.entity_name || '-' },
    { title: 'Services', key: 'services', render: (_, l) => (l.EngagementLetterServices || []).map((s) => s.description).join(', ') },
    { title: 'Signed', dataIndex: 'signed_at', render: (v, l) => (v ? `${fmtDate(v)} - ${l.signed_by_name}` : '-') },
    { title: 'Status', dataIndex: 'status', render: (v) => <Tag color={LETTER_STATUS_COLORS[v]}>{label(v)}</Tag> },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Engagement Letters
        </Typography.Title>
        <Space>
          {isSystemAdmin && (
            <Select style={{ width: 200 }} value={companyId} placeholder="Company" onChange={setCompanyId} options={companies.map((c) => ({ value: c.id, label: c.name }))} />
          )}
          <Select
            allowClear
            style={{ width: 160 }}
            placeholder="All statuses"
            value={statusFilter}
            onChange={setStatusFilter}
            options={Object.keys(LETTER_STATUS_COLORS).map((s) => ({ value: s, label: s }))}
          />
          {isStaff && (
            <Button
              type="primary"
              icon={<FileAddOutlined />}
              onClick={() => {
                form.resetFields();
                setEntities([]);
                setGenerateOpen(true);
              }}
            >
              Generate for Client
            </Button>
          )}
        </Space>
      </Space>

      <Table rowKey="id" columns={columns} dataSource={rows} loading={loading} />

      <Modal title="Generate Engagement Letter" open={generateOpen} onCancel={() => setGenerateOpen(false)} onOk={handleGenerate} confirmLoading={generating}>
        <Typography.Paragraph type="secondary">
          The draft lists every accepted proposal for the client that isn't already covered by a draft, sent or signed letter, with the standard
          responsibilities and disclaimers text ready to edit.
        </Typography.Paragraph>
        <Form form={form} layout="vertical">
          <Form.Item name="client_id" label="Client" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="label" onChange={handleClientChange} options={clients.map((c) => ({ value: c.id, label: c.client_name }))} />
          </Form.Item>
          <Form.Item name="entity_id" label="Legal Entity engaged" tooltip="Defaults to the client's primary entity">
            <Select allowClear options={entities.map((e) => ({ value: e.id, label: e.is_primary ? `${e.entity_name} (primary)` : e.entity_name }))} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
